import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useFieldArray, UseFormReturn, useWatch } from 'react-hook-form';
import { useDebounce } from '@features/onboarding/hooks/useDebounce';
import { HealthQuestion } from '@features/onboarding/components/6-health-question-form/interfaces';
import { FormField, FormItem, FormControl } from '@components/ui/form';
import { useHealthStore } from 'src/store/health';
import { MedicationTemplate } from 'src/services/interfaces/health';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
// import { t } from '@/i18n/index';
import { MedicationInput } from './MedicationInput';
import { MedicationList } from './MedicationList';
import { Trans } from 'react-i18next';

interface MedicationQuestionStepProps {
  question: HealthQuestion;
  form: UseFormReturn<any>;
  onMedicationsChange?: (medications: any[]) => void;
}

export const MedicationQuestionStep: React.FC<MedicationQuestionStepProps> = ({
  question,
  form,
  onMedicationsChange,
}) => {
  const { control, getValues } = form;
  const { fields, append, prepend, remove } = useFieldArray({
    control,
    name: `healthQuestions.${question.id}.medications` as any,
  });

  // Watch the medications array for changes
  const watchedMedications = useWatch({
    control,
    name: `healthQuestions.${question.id}.medications`,
    defaultValue: [],
  });

  // Debounce watchedMedications to prevent too frequent updates
  const debouncedWatchedMedications = useDebounce(watchedMedications, 300);

  const {
    getDrugInfoAutocomplete,
    isLoading,
    setError,
    error,
    isFailValidation,
    setFailValidation,
  } = useHealthStore();
  const [inputValue, setInputValue] = useState<string>('');
  const [suggestions, setSuggestions] = useState<MedicationTemplate[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debouncedInputValue = useDebounce(inputValue, 500);
  const onboardingData = useOnboardingStore((state) => state.data);
  const applicants = onboardingData?.applicants || [];
  const { setData } = useOnboardingStore();

  // Memoize form values to prevent unnecessary re-computations
  const currentHealthQuestions = useMemo(
    () => getValues('healthQuestions'),
    [getValues]
  );
  const currentMedications = useMemo(
    () => getValues(`healthQuestions.${question.id}`),
    [getValues, question.id]
  );

  // Memoize the fetch suggestions function
  const fetchSuggestions = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (inputValue && debouncedInputValue && debouncedInputValue.length >= 3) {
      try {
        abortControllerRef.current = new AbortController();
        const data = await getDrugInfoAutocomplete(debouncedInputValue);
        const existingNames = fields.map((field: any) =>
          (field.name || '').toLowerCase()
        );

        // Build list that excludes already-added names AND removes duplicates within the suggestions themselves
        const seenNames = new Set<string>();
        const uniqueSuggestions = [] as typeof data;
        for (const item of data) {
          const nameLower = (item.name || '').toLowerCase();
          // Skip if already in form
          if (existingNames.includes(nameLower)) continue;
          // Skip if duplicate in suggestions list
          if (seenNames.has(nameLower)) continue;
          seenNames.add(nameLower);
          uniqueSuggestions.push(item);
        }

        // Only update suggestions if they actually changed
        setSuggestions((prev) => {
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(uniqueSuggestions);
          return prevStr === newStr ? prev : uniqueSuggestions;
        });
      } catch (err: unknown) {
        if (err && (err as any).name !== 'AbortError') {
          console.error('Failed to fetch suggestions:', err);
        }
      } finally {
        abortControllerRef.current = null;
      }
    } else {
      setSuggestions([]);
    }
  }, [
    inputValue,
    debouncedInputValue,
    fields,
    getDrugInfoAutocomplete,
    setSuggestions,
  ]);

  useEffect(() => {
    fetchSuggestions();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchSuggestions]);

  useEffect(() => {
    setFailValidation(false);
  }, [inputValue, setInputValue]);

  const dynamicRecipientOptions = useMemo(
    () =>
      (
        [
          { value: 'primary', label: 'You' },
          ...applicants
            .filter((dep: any) => dep.applicant === 'spouse')
            .map((dep: any, i: number) => ({ value: `spouse_${i}`, label: dep.firstName || 'Spouse' })),
          ...applicants
            .filter((dep: any) => dep.applicant === 'dependent')
            .map((dep: any, idx: number) => ({
              value: `dependent_${idx}`,
              label: dep.firstName || `Dependent ${idx + 1}`,
            })),
        ] as { value: string; label: string }[]
      ).map((v, index) => ({
        value: `${index}`,
        label: v.label,
      })),
    [applicants]
  );

  const addSuggestion = useCallback(
    (suggestion: MedicationTemplate) => {
      const isAlreadyAdded = fields.some(
        (field: any) =>
          (field.name || '').toLowerCase() ===
          (suggestion.name || '').toLowerCase()
      );

      if (isAlreadyAdded) {
        return;
      }

      const defaultRecipient =
        dynamicRecipientOptions.length === 1
          ? dynamicRecipientOptions[0].value
          : '';

      prepend({
        name: suggestion.name,
        rxcui: suggestion.rxcui,
        form: suggestion.form,
        dosage: '',
        frequency: '',
        frequencyDescription: '',
        applicant: defaultRecipient,
        description: '',
      });
    },
    [fields, dynamicRecipientOptions, append]
  );

  // Memoize the update function to prevent unnecessary re-renders
  const updateStoreData = useCallback(() => {
    const currentStoreMedications =
      (onboardingData as any)?.healthQuestions?.[question.id]?.medications ||
      [];
    const medicationsChanged =
      JSON.stringify(currentStoreMedications) !==
      JSON.stringify(debouncedWatchedMedications);

    if (medicationsChanged) {
      setData({
        healthQuestions: {
          ...currentHealthQuestions,
          [question.id]: {
            ...currentMedications,
            medications: debouncedWatchedMedications,
          },
        },
      });
    }
  }, [
    setData,
    currentHealthQuestions,
    currentMedications,
    question.id,
    debouncedWatchedMedications,
    onboardingData,
  ]);

  useEffect(() => {
    // Update the parent component if a callback is provided
    if (onMedicationsChange) {
      onMedicationsChange(debouncedWatchedMedications);
    }

    // Update store data
    updateStoreData();
  }, [debouncedWatchedMedications, onMedicationsChange, updateStoreData]);

  return (
    <>
      <FormField
        name={`healthQuestions.${question.id}.medications` as any}
        control={control}
        render={({ field }) => (
          <FormItem className="relative mt-10 gap-0">
            <FormControl>
              <>
                <MedicationInput
                  field={field}
                  inputValue={inputValue}
                  suggestions={suggestions}
                  isLoading={isLoading}
                  error={error}
                  setInputValue={setInputValue}
                  setError={setError}
                  setSuggestions={setSuggestions}
                  addSuggestion={addSuggestion}
                />
                {!fields.length && isFailValidation && (
                  <p className="text-error font-semibold mt-4 text-center xl:text-left">
                    <Trans i18nKey="errors.specifyAnyMedication" />
                  </p>
                )}
              </>
            </FormControl>
          </FormItem>
        )}
      />
      {fields.length > 0 && (
        <div className="mt-4">
          Add another medication by searching above, or click{' '}
          <strong>Next</strong> to continue.
        </div>
      )}
      <MedicationList
        fields={fields}
        control={control}
        questionId={question.id}
        dynamicRecipientOptions={dynamicRecipientOptions}
        remove={remove}
        append={append}
      />
    </>
  );
};
